// Disconnect a player from the match they are in right now.
//
// This used not to be possible: Django has no channel to a running game server —
// the server calls us, never the reverse — so the standing advice was "ban them
// and restart the server", which ends everybody else's match too.
//
// A kick now rides back on the response to sync_online_players. The server polls
// that every ~30s, so a kick lands within one report cycle rather than instantly.
// The confirmation says so, because "did it work?" thirty seconds after pressing
// a button is how an operator learns to press it four more times.
//
// Delivered exactly once and cleared as it is handed over: there is no
// acknowledgement in the protocol, and repeating it on every report would
// disconnect somebody who had since been unbanned and legitimately rejoined.
//
// Ban and Kick are deliberately separate. Ban blocks the next login; Kick ends
// the current session. Most moderation wants both, but they are different
// promises and merging them would make one of them a lie.

import { faRightFromBracket } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { kickPlayer } from './api'

export const KickButton = ({ username, onAsk }: { username: string; onAsk: () => void }) => (
    <button
        onClick={onAsk}
        title={`Disconnect ${username} from this server. Does not block them rejoining — ban as well for that`}
        className='rounded border border-amber-400/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-400 hover:bg-amber-400/10'
    >
        <FontAwesomeIcon icon={faRightFromBracket} className='mr-1' />
        Kick
    </button>
)

export const KickConfirm = ({
    serverId,
    userId,
    username,
    onDone,
    onCancel,
}: {
    serverId: number
    userId: number
    username: string
    onDone?: () => void
    onCancel: () => void
}) => {
    const [busy, setBusy] = useState(false)
    const { addNotification } = useNotification()

    const run = async () => {
        setBusy(true)
        try {
            await kickPlayer(serverId, [userId])
            addNotification(
                `${username} will be disconnected on the server's next report — within ~30s.`,
                'success'
            )
            onDone?.()
        } catch (err: any) {
            // The 501 case is worth surfacing verbatim: it means kicking is
            // switched off fleet-wide, which is a setting, not a fault here.
            addNotification(err.message, 'error')
        } finally {
            setBusy(false)
            onCancel()
        }
    }

    return (
        <div className='w-full min-w-0 whitespace-normal'>
            <ConfirmBar
                message={`Kick ${username} from this server?`}
                detail={
                    'Disconnects them from the match they are in. Not instant: the ' +
                    'instruction rides back on the server’s next roster report, so it ' +
                    'lands within about 30 seconds. ' +
                    'It does not stop them rejoining — ban them as well if that is what ' +
                    'you want. Their session closes normally, so anything they earned up ' +
                    'to this point is still recorded.'
                }
                confirmLabel='Kick'
                onConfirm={run}
                onCancel={onCancel}
                busy={busy}
            />
        </div>
    )
}
